import { Component, Input, OnChanges } from '@angular/core';
import { QueryParams, ReasonStat, REASONS, Colors, ColorsTrend } from "../../exit-survey.interfaces";
import { Chart } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { ExitSurveyService } from '../../services/exit-survey.service';
const moment = require("moment");
const alertify = require("alertify.js");

@Component({
  selector: 'ch-exit-survey-statistics',
  templateUrl: './exit-survey-statistics.component.html',
  styleUrls: ['./exit-survey-statistics.component.css'],
})
export class ExitSurveyStatisticsComponent implements OnChanges {
  @Input() public pageParams: QueryParams;

  public reasonStats: ReasonStat[] = [];
  public reasonsChart = null as Chart;
  public reasonsComparisonPieChart = null as Chart;

  constructor(
    private exitSurveyService: ExitSurveyService
  ) { }

  ngOnChanges(): void {
    this.loadReasons(this.pageParams);
  }

  private loadReasons(params: QueryParams) {
    this.exitSurveyService.getSurveyReasonStats(params)
      .subscribe(
      result => {
        const data = result.json()
        this.reasonStats = data.map(
          (reason: ReasonStat) => {
            return {
              reason: REASONS.get(reason.reason),
              count: reason.count,
              date: reason.date,
            }
          }
        );
        this.updateReasonChartData();
        this.updateReasonComparisonPieChartData();
      },
      error => {
        alertify.error(error)
      });
  }

  get aggregateCounts(): ReasonStat[] {
    const aggregated = [];

    this.reasonStats.forEach(reasonStat => {
      const reason = reasonStat.reason;
      const existingEntry = aggregated.find(entry => entry.reason === reason);

      if (existingEntry) {
        existingEntry.count += reasonStat.count;
      } else {
        aggregated.push({ reason: reason, count: reasonStat.count });
      }
    });

    return aggregated;
  }

  get aggregateReasonCountsByMonth() {
    let months = this.reasonStats.map(item => item.date)
    months = months.filter((item, index) => months.indexOf(item) === index)
      .map(item => moment(item))
      .sort((a, b) => a.unix() - b.unix());

    let reasons = this.reasonStats.map(item => item.reason)
    reasons = reasons.filter((item, index) => reasons.indexOf(item) === index);

    const aggregated = [];

    reasons.forEach(reason => {
      months.forEach(month => {
        const entry = this.reasonStats.find(item => item.reason === reason && moment(item.date).isSame(month));
        const count = entry ? +entry.count : 0;
        const existingEntry = aggregated.find(entry => entry.reason === reason);

        if (existingEntry) {
          if (!existingEntry.data) {
            existingEntry.data = [];
          }
          existingEntry.data.push(count);
        } else {
          aggregated.push({ reason: reason, data: [count] });
        }
      })
    });

    return aggregated;
  }

  private calculateTrendLine(yValues: number[]) {
    if (!Array.isArray(yValues) || yValues.length < 2) {
      return [];
    }

    const n = yValues.length;
    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumX2 = 0;

    for (let i = 0; i < n; i++) {
      sumX += i;
      sumY += yValues[i];
      sumXY += i * yValues[i];
      sumX2 += i * i;
    }

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    const trendLineYValues = [];
    for (let i = 0; i < n; i++) {
      const x = i;
      const y = slope * x + intercept;
      trendLineYValues.push(y);
    }

    return trendLineYValues;
  }

  public getTimePeriodText(): string {
    if (!this.pageParams) {
      return 'all time';
    }

    // Check for special cases: last 3 months and year to date
    if (this.pageParams.from && !this.pageParams.until) {
      const fromDate = moment(this.pageParams.from);

      // Check if it's exactly 3 months ago from current date (month and year comparison only)
      const threeMonthsAgo = moment().subtract(3, 'months');
      if (fromDate.isSame(threeMonthsAgo, 'month')) {
        return 'last 3 months';
      }

      // Check if it's year to date (January of current year - month and year comparison only)
      const yearStart = moment().startOf('year');
      if (fromDate.isSame(yearStart, 'month')) {
        return 'year to date';
      }

      return `since ${fromDate.format('MMM YYYY')}`;
    }

    if (this.pageParams.from && this.pageParams.until) {
      const fromDate = moment(this.pageParams.from).format('MMM YYYY');
      const untilDate = moment(this.pageParams.until).format('MMM YYYY');
      if (fromDate === untilDate) {
        return fromDate;
      }
      return `${fromDate} to ${untilDate}`;
    }

    if (this.pageParams.until) {
      const untilDate = moment(this.pageParams.until).format('MMM YYYY');
      return `until ${untilDate}`;
    }

    return 'all time';
  }

  private updateReasonChartData() {
    let months = this.reasonStats.map(item => item.date)
    months = months.filter((item, index) => months.indexOf(item) === index)
      .map(item => moment(item))
      .sort((a, b) => a.unix() - b.unix());

    const datasets = [];
    this.aggregateReasonCountsByMonth.map((entry, index) => {
      return [
        {
          label: entry.reason,
          data: entry.data.map((count, mIndex) => {
            return {
              x: months[mIndex],
              y: count
            }
          }),
          borderColor: Colors[index % Colors.length],
          backgroundColor: Colors[index % Colors.length],
          fill: false,
          lineTension: 0.25,
        },
        {
          label: `${entry.reason} Trend`,
          data: this.calculateTrendLine(entry.data).map((count, mIndex) => {
            return {
              x: months[mIndex],
              y: count
            }
          }),
          borderColor: ColorsTrend[index % Colors.length],
          backgroundColor: ColorsTrend[index % Colors.length],
          fill: false,
          borderWidth: 1.5,
          borderDash: [6, 2],
          lineTension: 0,
        }
      ]
    }).forEach(entry => datasets.push(...entry));

    const chartConfig = {
      type: 'line',
      data: {
        datasets,
      },
      options: {
        // Since some plugins autoregister globally, the workaround is to disable them on per chart basis
        plugins: {
          datalabels: false
        },
        legend: {
          position: 'left',
        },
        elements: {
          point: {
            radius: 0,
          },
        },
        colors: {
          enabled: true
        },
        title: {
          display: false,
        },
        scales: {
          xAxes: [{
            type: 'time',
            time: {
              unit: 'month',
              displayFormats: {
                month: 'MMM YYYY'
              }
            },
            labels: months,
          }],
          yAxes: [{
            ticks: {
              beginAtZero: true,
              max: this.reasonStats.map(i => i.count).reduce((a, b) => Math.max(a, b), -Infinity) + 1,
            }
          }]
        }
      }
    };

    if (this.reasonsChart) {
      this.reasonsChart.destroy();
    }
    const ctx = (document.getElementById('reasonsChartCanvas') as HTMLCanvasElement).getContext('2d');
    this.reasonsChart = new Chart(ctx, chartConfig)
  }

  get aggregateCountsByMonthL3M() {
    const l3mDate = moment().startOf('month').subtract(3, 'months');

    let reasons = this.reasonStats.map(item => item.reason)
    reasons = reasons.filter((item, index) => reasons.indexOf(item) === index);

    const aggregated = [];

    reasons.forEach(reason => {
      const data = this.reasonStats.filter(item => item.reason === reason && moment(item.date).isSameOrAfter(l3mDate));
      const count = data.map(item => +item.count).reduce((a, b) => a + b, 0);

      aggregated.push({ reason: reason, data: count });
    });

    return aggregated;
  }

  private updateReasonComparisonPieChartData() {
    const aggl3m = this.aggregateCountsByMonthL3M;
    const data = {
      labels: aggl3m.map(entry => entry.reason),
      datasets: [{ data: aggl3m.map(entry => entry.data), backgroundColor: Colors }],
    };

    const chartConfig = {
      type: 'pie',
      data: data,
      options: {
        legend: {
          position: 'right',
        },
        colors: {
          enabled: true
        },
        title: {
          display: false,
        },
        plugins: {
          datalabels: {
            formatter: (value, ctx) => {
              let sum = 0;
              let dataArr = ctx.chart.data.datasets[0].data;
              dataArr.map(data => {
                sum += data;
              });
              let percentage = (value * 100 / sum).toFixed(0) + "%";
              return percentage;
            },
            color: '#fff',
            font: {
              weight: 'bold',
            },
            display: function(context) {
              return context.dataset.data[context.dataIndex] > 0;
            },
            anchor: 'end',
            align: 'start',
            offset: 25
          }
        },
      },
      plugins: [ChartDataLabels],
    };

    if (this.reasonsComparisonPieChart) {
      this.reasonsComparisonPieChart.destroy();
    }
    const ctx = (document.getElementById('reasonsComparisonPieChartCanvas') as HTMLCanvasElement).getContext('2d');
    this.reasonsComparisonPieChart = new Chart(ctx, chartConfig)
  }
}



// WEBPACK FOOTER //
// /Users/thxgg/Projects/Work/code-hospitality-cms/src/app/exit-survey/exit-survey-list/exit-survey-statistics/exit-survey-statistics.component.ts